import { Typography } from "@mui/material";
import moment from "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { updateAirportOptions } from "src/utils/airportHelper";

// alias typography props here for easier referencing
type TypographyProps = React.ComponentProps<typeof Typography>;

interface IDateDisplayProps {
  /** Date to display */
  date: Date | number;
  /** Timezone of this date, if none specified then UTC is assumed */
  tz?: string;
  /** Whether or not the time should be displayed. Defaults to true if unspecified. */
  showTime?: boolean;
  /** Whether or not the timezone should be displayed. Defaults to true if unspecified. */
  showTimeZone?: boolean;
  /** Properties to pass to the Typography component. If none specified, "inherit" variant is used. */
  typographyProps?: TypographyProps;
  /** If timezone is passed in as an ICAO value, retrieve it's timezone from the airport service. */
  tzICAO?: string;
  /** Whether or not the date should be displayed. Defaults to true if unspecified. */
  showDate?: boolean;
}

/** The IANA Code for UTC Timezone */
const UTC_IANA = "Etc/UTC";

/**
 * Format a date object or epoch number as a standardized date string.
 *
 * @remarks
 * Try using DateDisplay before resorting to this function.
 *
 * @param date - Date or epoch to format as string
 * @param zone - Timezone the date should be in
 * @param showTime - Whether or not to show the time with the date
 * @param showTimeZone - Wehter or not to show the timezone
 */
export const formatDateString = (
  date: Date | number,
  zone: string,
  showTime = true,
  showTimeZone = true,
  showDate = true
): string => {
  const momentWithZone: moment.Moment = moment.utc(date).tz(zone);
  const timeStrFormat: string = showTime ? " HH:mm" : "";
  const zoneStrFormat: string = showTimeZone ? " z" : "";
  const dateStrFormat: string = showDate ? "MMM Do, Y" : "";
  const formattedStr: string = momentWithZone.format(`${dateStrFormat}${timeStrFormat}${zoneStrFormat}`);
  return formattedStr;
};

/**
 * Converts a Date or epoch number to a formatted string and displays it with the [[Typography]] component.
 * Throws an error if supplied timezone string is invalid
 * @param props - [[IDateDisplayProps]]
 */
export const DateDisplay = (props: IDateDisplayProps): JSX.Element => {
  const { date, tz, showTime, showTimeZone, typographyProps, tzICAO, showDate } = props;

  const dispatch = useDispatch();
  const structures = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  //const zone: string = tz ?? UTC_IANA;
  const [zone, setZone] = useState<string>(tz ?? UTC_IANA);
  const typographyProperties = typographyProps ?? { variant: "inherit" };

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // If timezone information is passed in as an ICAO code and tz isn't available, retrieve that airport info
  useEffect(() => {
    if (tzICAO && !tz) {
      loadAirportData(tzICAO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tzICAO, tz]);

  // If airport options are updated or already contain timezone info for a given tzICAO, load it in.
  useEffect(() => {
    if (!!airportOptions[tzICAO]?.timezoneId && !tz) {
      setZone(airportOptions[tzICAO]?.timezoneId);
    } else if (!airportOptions[tzICAO]?.timezoneId && !tz) {
      setZone(UTC_IANA);
    }
  }, [airportOptions, tz, tzICAO]);

  useEffect(() => {
    if (tz) setZone(tz);
  }, [tz]);

  const dateString: string = formatDateString(date, zone, showTime, showTimeZone, showDate);

  return <Typography {...typographyProperties}>{dateString}</Typography>;
};

interface ITimeDisplayProps {
  time: string;
  tz?: string;
  tzICAO?: string;
  /** Properties to pass to the Typography component. If none specified, "inherit" variant is used. */
  typographyProps?: TypographyProps;
}
/**
 * Converts a Date or epoch number to a formatted string and displays it with the [[Typography]] component.
 * Throws an error if supplied timezone string is invalid
 * @param props - [[ITimeDisplayProps]]
 */
export const TimeDisplay = (props: ITimeDisplayProps): JSX.Element => {
  const { time, tz, tzICAO, typographyProps } = props;
  const dispatch = useDispatch();
  const structures = useSelector((state: IRootState) => state.structures);
  const { airportOptions } = structures;

  //const zone: string = tz ?? UTC_IANA;
  const [zone, setZone] = useState<string>(tz ?? UTC_IANA);
  const typographyProperties = typographyProps ?? { variant: "inherit" };

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // If timezone information is passed in as an ICAO code and tz isn't available, retrieve that airport info
  useEffect(() => {
    if (tzICAO) {
      loadAirportData(tzICAO);
    }
  }, [tzICAO, loadAirportData]);

  // If airport options are updated or already contain timezone info for a given tzICAO, load it in.
  useEffect(() => {
    if (!!airportOptions[tzICAO]?.timezoneId && !tz) {
      setZone(airportOptions[tzICAO]?.timezoneId);
    } else if (!airportOptions[tzICAO]?.timezoneId && !tz) {
      setZone(UTC_IANA);
    }
  }, [airportOptions, tz, tzICAO]);

  useEffect(() => {
    if (tz) setZone(tz);
  }, [tz]);
  const s = new Date();
  const selectedDate = `${s.getUTCFullYear()}-${s.getUTCMonth()}-${s.getUTCDate()} ${time}`;
  const timeString = `${time} ${moment.tz(selectedDate, zone).format("z")}`;

  return <Typography {...typographyProperties}>{timeString}</Typography>;
};
